import { useMemo } from "react";
import menuConfigData from "../config/menu-config.json";
import { MenuConfig, MenuItem, UserRole } from "../types/menu.types";

/**
 * Custom hook to manage menu configuration based on user role
 */
export const useMenuConfig = (userRole: UserRole | string) => {
  const menuConfig = menuConfigData as MenuConfig;

  /**
   * Filter menu items based on user role
   */
  const filterMenuByRole = useMemo(() => {
    const filterItems = (items: MenuItem[]): MenuItem[] => {
      return items
        .filter((item) => item.roles.includes(userRole as UserRole))
        .map((item) => ({
          ...item,
          children: item.children ? filterItems(item.children) : undefined,
        }))
        .filter((item) => !item.children || item.children.length > 0)
        .sort((a, b) => (a.order || 999) - (b.order || 999));
    };

    return filterItems(menuConfig.menuItems);
  }, [userRole]);

  /**
   * Get quick actions for the current user role
   */
  const quickActions = useMemo(() => {
    return menuConfig.quickActions[userRole as UserRole] || [];
  }, [userRole]);

  /**
   * Get role configuration
   */
  const roleConfig = useMemo(() => {
    return (
      menuConfig.roleConfig[userRole as UserRole] || {
        displayName: userRole,
        icon: "Person",
        color: "#6b7280",
      }
    );
  }, [userRole]);

  /**
   * Check if user has access to a specific menu item
   */
  const hasAccess = (menuItemId: string): boolean => {
    const findItem = (items: MenuItem[]): boolean => {
      for (const item of items) {
        if (
          item.id === menuItemId &&
          item.roles.includes(userRole as UserRole)
        ) {
          return true;
        }
        if (item.children && findItem(item.children)) {
          return true;
        }
      }
      return false;
    };

    return findItem(menuConfig.menuItems);
  };

  /**
   * Search menu items by title
   */
  const searchMenu = (query: string): MenuItem[] => {
    if (!query.trim()) return filterMenuByRole;

    const searchTerm = query.toLowerCase();
    const results: MenuItem[] = [];

    const searchItems = (items: MenuItem[]) => {
      for (const item of items) {
        if (
          item.title.toLowerCase().includes(searchTerm) &&
          item.roles.includes(userRole as UserRole)
        ) {
          results.push(item);
        }
        if (item.children) {
          searchItems(item.children);
        }
      }
    };

    searchItems(menuConfig.menuItems);
    return results;
  };

  /**
   * Get all available paths for the current user
   */
  const getAvailablePaths = (): string[] => {
    const paths: string[] = [];

    const extractPaths = (items: MenuItem[]) => {
      for (const item of items) {
        if (item.path && item.roles.includes(userRole as UserRole)) {
          paths.push(item.path);
        }
        if (item.children) {
          extractPaths(item.children);
        }
      }
    };

    extractPaths(menuConfig.menuItems);
    return paths;
  };

  /**
   * Get breadcrumb trail for a given path
   */
  const getBreadcrumb = (currentPath: string): MenuItem[] => {
    const breadcrumb: MenuItem[] = [];

    const findPath = (items: MenuItem[], trail: MenuItem[]): boolean => {
      for (const item of items) {
        const newTrail = [...trail, item];

        if (item.path === currentPath) {
          breadcrumb.push(...newTrail);
          return true;
        }

        if (item.children && findPath(item.children, newTrail)) {
          return true;
        }
      }
      return false;
    };

    findPath(filterMenuByRole, []);
    return breadcrumb;
  };

  return {
    menuItems: filterMenuByRole,
    quickActions,
    roleConfig,
    hasAccess,
    searchMenu,
    getAvailablePaths,
    getBreadcrumb,
  };
};
